import { daysBetween } from './dates';
import { capitalize } from './format';

// Turns the server's cycle timeline into per-day lookups. FlexBoard decides no
// cycle rule itself (README §7: "the server is the source of truth for the
// cycle"); it only reads which timeline entry and phase span a date falls in.

/** Days before / after a period that give the summary cards their Before / After state. */
export const PRE_PERIOD_DAYS = 7;

/**
 * @typedef {Object} Cycle
 * @property {number} n - 1-based, counted from the oldest logged cycle fetched.
 * @property {string} start
 * @property {string} periodEnd
 * @property {number} periodDays - start…periodEnd, inclusive.
 * @property {string|null} nextStart - The recorded next start; null for the current cycle.
 * @property {number|null} length - Completed cycles only.
 * @property {boolean} completed
 * @property {Array<{phase: string, start_date: string, end_date: string}>} phases
 */

/**
 * Logged (not predicted) cycles that started on or before `today`, oldest
 * first. The last one is the current cycle, as in `cycles_analytics`.
 * @returns {Cycle[]}
 */
export function loggedCycles(periodSummary, today) {
  const logged = (periodSummary?.timeline || []).filter((c) => !c.predicted && c.start_date <= today);
  return logged.map((c, i) => {
    const completed = i < logged.length - 1;
    const periodEnd = c.period_end || c.start_date;
    return {
      n: i + 1,
      start: c.start_date,
      periodEnd,
      periodDays: daysBetween(c.start_date, periodEnd) + 1,
      nextStart: completed ? logged[i + 1].start_date : null,
      length: completed ? c.length ?? daysBetween(c.start_date, logged[i + 1].start_date) : null,
      completed,
      phases: c.phases || [],
    };
  });
}

/**
 * Where a day sits in the cycle.
 * @returns {null | {
 *   cycle: Cycle, cd: number, per: boolean,
 *   phase: string|null, phaseKey: string|null
 * }} null before the first logged period and after `today`.
 */
export function dayInfo(cycles, iso, today) {
  if (iso > today) return null;
  let cycle = null;
  for (let i = cycles.length - 1; i >= 0; i--) {
    if (cycles[i].start <= iso) {
      cycle = cycles[i];
      break;
    }
  }
  if (!cycle) return null;
  const cd = daysBetween(cycle.start, iso) + 1;
  const per = iso <= cycle.periodEnd;
  const span = cycle.phases.find((p) => p.start_date <= iso && iso <= p.end_date);
  const phase = span ? capitalize(span.phase) : per ? 'Menstrual' : null;
  // The current cycle's luteal days wait on the next period to be confirmed.
  const estimated = !cycle.completed && (phase === 'Luteal' || phase == null);
  const phaseKey = estimated ? 'est' : phase;
  return { cycle, cd, per, phase, phaseKey };
}

/**
 * Which state the summary cards take, checked in this order:
 * during a period → before one (within 7 days, or late) → the 7 days after
 * one → today.
 * @returns {'during'|'before'|'after'|'today'}
 */
export function cardState(periodSummary, today) {
  if (periodSummary?.current_period) return 'during';
  const until = periodSummary?.next_period?.days_until;
  if (until != null && until <= PRE_PERIOD_DAYS) return 'before';
  const periods = periodSummary?.periods || [];
  const last = periods[periods.length - 1];
  if (last?.end_date) {
    const since = daysBetween(last.end_date, today);
    if (since >= 1 && since <= PRE_PERIOD_DAYS) return 'after';
  }
  return 'today';
}
